import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulbOn } from '@fortawesome/pro-solid-svg-icons/faLightbulbOn';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons/faArrowsRotate';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { noop } from 'ui-components/default-props/noop';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import { setAutomaticSync, syncConsolidatedInventoryCounts, bulkUpdateConnections } from './api';
import { EditInventorySync } from './edit-inventory-sync';
import { InventoryConnectionTerminusTypeEnum, InventoryConnectionJobStatusEnum } from './enums';
import { InventoryConnectionActions } from './inventory-connection-actions/inventory-connection-actions';
import InventorySetupPanel from './inventory-setup-panel';
import styles from './inventory-sync-page.module.less';
import InventorySyncPlaceholder from './inventory-sync-placeholder.png';
import InventorySyncSetupPanel from './inventory-sync-setup-panel';
import messages from './messages';
import useConsolidatedInventorySources from './use-consolidated-inventory-sources';
import DataDrivenLogo from '../../../components/data-driven-logo';
import HelpArticleLink from '../../../components/help-article-link';
import {
  INVENTORY_SYNC_AUTO_SYNC_TOGGLE,
  INVENTORY_SYNC_AUTO_SYNC_TOGGLE_ERROR,
  INVENTORY_SYNC_MANUAL_SYNC_CLICK,
  INVENTORY_SYNC_MANUAL_SYNC_ERROR,
} from '../../../constants/analytics-events';
import { SellerFeatures } from '../../../enums/seller-features';
import { useIsSellerFeatureEnabled } from '../../../hooks/use-is-seller-feature-enabled';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { Segment } from '../../../services/analytics-services/segment';
import { useSimpleToggle } from '../../../hooks/use-simple-toggle';
import { linkIDs } from '../../../services/link-service/links';
import Global from '../../../store/global-selectors';
import { Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import settingsPageStyle from '../settings-page.module.less';

const useToggleAutoSync = (isAutomaticSyncEnabled) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoSyncEnabled, , toggleAutoSync] = useSimpleToggle();

  useEffect(() => {
    toggleAutoSync(isAutomaticSyncEnabled);
  }, [isAutomaticSyncEnabled, toggleAutoSync]);

  const onToggleAutoSync = async (isEnabled, finallyFn) => {
    setIsLoading(true);
    toggleAutoSync(isEnabled);

    try {
      await setAutomaticSync(isEnabled);
      Segment.trackEvent(INVENTORY_SYNC_AUTO_SYNC_TOGGLE, {
        toggle_to: isEnabled,
      });
    } catch (error) {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.cantSetAutoSyncTitle} />,
        content: <FormattedMessage {...messages.cantSetAutoSyncContent} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        dontHide: true,
      });
      // lets revert the toggle if there is an error
      // to keep the UI in sync with the backend
      toggleAutoSync(!isEnabled);

      Segment.trackEvent(INVENTORY_SYNC_AUTO_SYNC_TOGGLE_ERROR, {
        toggle_to: isEnabled,
        error,
      });
    } finally {
      setIsLoading(false);
      finallyFn();
    }
  };

  return {
    isAutoSyncLoading: isLoading,
    isAutoSyncEnabled,
    onToggleAutoSync,
  };
};

const useManualSync = () => {
  const [isLoading, setIsLoading] = useState(false);

  const onInventorySync = async () => {
    try {
      setIsLoading(true);
      await syncConsolidatedInventoryCounts();

      // 1 minute
      const timeToEnableBtn = 60000;
      setTimeout(() => {
        setIsLoading(false);
      }, timeToEnableBtn);

      NotificationService.showNotification({
        title: <FormattedMessage {...messages.manualSyncTitleSuccess} />,
        content: <FormattedMessage {...messages.manualSyncContentSuccess} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        dontHide: true,
      });

      Segment.trackEvent(INVENTORY_SYNC_MANUAL_SYNC_CLICK, {});
    } catch (error) {
      setIsLoading(false);
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.manualSyncTitleFail} />,
        content: <FormattedMessage {...messages.manualSyncContentFail} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        dontHide: true,
      });

      Segment.trackEvent(INVENTORY_SYNC_MANUAL_SYNC_ERROR, {
        error,
      });
    }
  };

  return {
    isLoading,
    onInventorySync,
  };
};

const InventorySyncSettingsWrapper = ({
  children,
  showContainer = true,
  noConnectionsSetup = false,
  handleEditClick = noop,
  isSyncButtonDisabled = false,
  isSystemAdmin = false,
  onSyncClicked = noop,
}) => {
  return (
    <div className={classnames(settingsPageStyle.content, styles['inventory-sync-page-content'])}>
      <SettingsPageTitle value={messages.inventorySyncPageTitle} />
      <div className={styles['inventory-sync-wrapper']}>
        <FlexColumn className={styles['page-content']}>
          <BannerMessageV2
            buttons={
              <Button href='https://form.jotform.com/241495724938166' theme='secondary'>
                <FormattedMessage {...messages.shareFeedbackButton} />
              </Button>
            }
            className={styles['beta-banner']}
            content={
              <Text
                formattedMessageValues={{
                  a: (chunks) => <HelpArticleLink articleId={linkIDs.InventorySyncTargetsFAQ}>{chunks}</HelpArticleLink>,
                }}
                textType={TextType.Paragraph}
                value={{ ...messages.betaBannerBody }}
              />
            }
            customIcon={<FontAwesomeIcon icon={faLightbulbOn} />}
            layout={BannerLayout.Full}
            status={BannerStatus.Custom}
          />
          <div className={styles['page-description']}>
            <Text textType={TextType.Paragraph} value={messages.inventorySyncHelperText} />
          </div>
        </FlexColumn>
        {showContainer ? (
          <div className={styles['inventory-sync-container']}>
            <div className={styles['header-row']}>
              <div className={styles['overview-header']}>
                <FormattedMessage {...messages.currentConfiguration} />
              </div>
              <Button disabled={noConnectionsSetup} onClick={handleEditClick} theme='link'>
                <div className={styles['edit-link']}>
                  <FontAwesomeIcon className={styles['pencil-icon']} icon={faPen} />
                  <FormattedMessage {...messages.inventorySyncEditButton} />
                </div>
              </Button>
            </div>
            {children}
          </div>
        ) : null}
        {!showContainer && children}
        {!noConnectionsSetup && isSystemAdmin ? (
          <div className={styles['sync-button-container']}>
            <Button
              className={styles['sync-consolidated-inventory-button']}
              disabled={isSyncButtonDisabled}
              onClick={onSyncClicked}
              theme='primary'
            >
              <FormattedMessage {...messages.syncConsolidatedInventoryButton} />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const InventorySyncPage = () => {
  const intl = useIntl();
  const isSystemAdmin = useSelector(Global.selectUserisSysAdmin);
  const { fetchConsolidatedInventorySources, fetchConsolidatedInventoryConnections, loading, connections, data } =
    useConsolidatedInventorySources();

  useOnComponentMount(() => {
    fetchConsolidatedInventorySources();
  });
  useOnComponentMount(() => {
    fetchConsolidatedInventoryConnections();
  });

  const [moveableConnections, setMoveableConnections] = useState(connections);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncButtonDisabled, setIsSyncButtonDisabled] = useState(false);

  const marketplaces = useSelector(Global.selectReferenceDatagetMarketplaces);
  const allFulfillmentProviders = useSelector(Global.selectReferenceDatagetFulfillmentProviders);
  const inventorySyncFeatureEnabled = useIsSellerFeatureEnabled(SellerFeatures.InventorySync);

  const getLogoProps = (apiCode) => {
    const marketplace = marketplaces.find((m) => m.apiCode === apiCode);
    if (marketplace) {
      return { marketplaceId: marketplace.marketplaceId };
    }

    const fulfillmentProvider = allFulfillmentProviders.find((fp) => fp.apiCode === apiCode);
    if (fulfillmentProvider) {
      return { fillProviderId: fulfillmentProvider.fillProviderId };
    }

    return { marketplaceId: 0 };
  };

  const renderSyncStatus = (conn) => {
    const hasLastRefreshedAt = conn.lastRefreshedAt;
    const lastJobInfo = conn.lastJobInfo ? JSON.parse(conn.lastJobInfo) : null;
    const jobStatus = lastJobInfo ? lastJobInfo.status : null;

    if (jobStatus === InventoryConnectionJobStatusEnum.failed || !hasLastRefreshedAt) {
      return (
        <div className={styles['connection-sync-error']}>
          <FontAwesomeIcon className={styles['error-icon']} icon={faExclamationCircle} />
          <FormattedMessage {...messages.errorSyncing} />

          <HelpArticleLink articleId={linkIDs.InventorySyncTargetsFAQ}>
            <FormattedMessage {...messages.errorLink} />
          </HelpArticleLink>
        </div>
      );
    }

    return (
      <div className={styles['connection-last-synced']}>
        <FormattedMessage {...messages.lastSynced} /> {new Date(conn.lastRefreshedAt).toLocaleString()}
      </div>
    );
  };

  useEffect(() => {
    // filter connections to not include disabled ones
    setMoveableConnections((connections || []).filter((connection) => connection.status !== 'Disabled'));
  }, [connections]);

  const sourceConnections = moveableConnections.filter(
    (connection) => connection.terminusType === InventoryConnectionTerminusTypeEnum.Source,
  );
  const targetConnections = moveableConnections.filter(
    (connection) => connection.terminusType === InventoryConnectionTerminusTypeEnum.Target,
  );

  const noConnectionsSetup = sourceConnections.length === 0 && targetConnections.length === 0;

  const trackEditProcess = useTrackWorkflow(Workflows.EDIT_INVENTORY_SYNC_FLOW);

  const handleEditClick = () => {
    setIsEditing(true);
    trackEditProcess(WorkflowSteps.START);
  };

  const getTerminusType = (connection) => {
    if (connection.isCheckedSource) return InventoryConnectionTerminusTypeEnum.Source;
    if (connection.isCheckedTarget) return InventoryConnectionTerminusTypeEnum.Target;
    return InventoryConnectionTerminusTypeEnum.None;
  };

  const updateNotification = (notificationId, notificationType, title, content) => {
    NotificationService.updateNotification(notificationId, {
      title,
      content,
      notificationType,
      progress: false,
      dontHide: false,
    });
  };

  const showSyncNotification = (title, message, notificationType) => {
    const notificationInfo = {
      title: <FormattedMessage {...title} />,
      content: <FormattedMessage {...message} />,
      notificationType,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    };

    return NotificationService.showNotification(notificationInfo);
  };

  const onSyncClicked = async () => {
    try {
      setIsSyncButtonDisabled(true);
      const response = await syncConsolidatedInventoryCounts();
      if (response.status !== 200) {
        showSyncNotification(messages.syncErrorTitle, messages.syncError, NOTIFICATION_TYPE_ERROR);
      } else {
        showSyncNotification(messages.syncSuccessfullyStartedTitle, messages.syncSuccessfullyStarted, NOTIFICATION_TYPE_INFO);
      }
    } catch {
      showSyncNotification(messages.syncErrorTitle, messages.syncError, NOTIFICATION_TYPE_ERROR);
    } finally {
      setTimeout(() => {
        setIsSyncButtonDisabled(false);
      }, 1000 * 60);
      await fetchConsolidatedInventoryConnections();
    }
  };

  const handleCancel = () => {
    trackEditProcess(WorkflowSteps.CANCEL);
    setIsEditing(false);
  };

  const handleSave = async (updatedConnections) => {
    setIsLoading(true);

    const notificationId = NotificationService.showNotification({
      title: <FormattedMessage {...messages.updateConnectionsInProgressTitle} />,
      content: <FormattedMessage {...messages.updateConnectionsInProgressContent} />,
      notificationType: NOTIFICATION_TYPE_INFO,
      progress: true,
      dontHide: true,
    });

    const updateConnections = async () => {
      const updateConnectionRequests = updatedConnections.map((conn) => ({
        connectionId: conn.connectionId,
        terminusType: getTerminusType(conn),
      }));
      // so basically there is no need to sync if there is no target
      const isInventorySyncRequired = updateConnectionRequests.some(
        (conn) => conn.terminusType === InventoryConnectionTerminusTypeEnum.Target,
      );

      let isSuccessful = true;
      try {
        await bulkUpdateConnections(updateConnectionRequests);
        if (!isInventorySyncRequired) {
          updateNotification(
            notificationId,
            NOTIFICATION_TYPE_SUCCESS,
            <FormattedMessage {...messages.updateConnectionsSuccessTitle} />,
            <FormattedMessage {...messages.updateConnectionsSuccessContent} />,
          );
        }
        trackEditProcess(WorkflowSteps.COMPLETE, {
          targetCount: updateConnectionRequests.filter((conn) => conn.terminusType === InventoryConnectionTerminusTypeEnum.Target).length,
          sourcesCount: updateConnectionRequests.filter((conn) => conn.terminusType === InventoryConnectionTerminusTypeEnum.Source).length,
        });
      } catch (error) {
        isSuccessful = false;
        updateNotification(
          notificationId,
          NOTIFICATION_TYPE_ERROR,
          <FormattedMessage {...messages.updateConnectionsErrorTitle} />,
          <FormattedMessage {...messages.updateConnectionsErrorContent} />,
        );
        trackEditProcess(WorkflowSteps.ERROR, {
          oldConnection: moveableConnections,
          newConnection: updateConnectionRequests,
          error,
        });
      } finally {
        setIsEditing(false);
      }

      return {
        isSuccessful,
        isInventorySyncRequired,
      };
    };

    const { isSuccessful, isInventorySyncRequired } = await updateConnections();

    if (isSuccessful) {
      await fetchConsolidatedInventoryConnections();
    }

    if (!isInventorySyncRequired || !isSuccessful) {
      setIsLoading(false);
      return;
    }

    try {
      await syncConsolidatedInventoryCounts();
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_SUCCESS,
        <FormattedMessage {...messages.startSyncSuccessTitle} />,
        <FormattedMessage {...messages.startSyncSuccessContent} />,
      );
    } catch {
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_ERROR,
        <FormattedMessage {...messages.updateConnectionsErrorTitle} />,
        <FormattedMessage {...messages.updateConnectionsErrorContent} />,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // this will be used when backend is done
  // todo: https://auctane.atlassian.net/browse/SPD-18057
  const {
    onToggleAutoSync,
    // enableAutoSync,
    // isAutoSyncLoading,
  } = useToggleAutoSync(data.isAutomaticSyncEnabled);

  // const isAutoSyncEnabled = !isAutoSyncLoading;

  const { onInventorySync, isLoading: onInventorySyncLoading } = useManualSync();

  if (loading) {
    return (
      <InventorySyncSettingsWrapper showContainer>
        <LoadingIndicator />
      </InventorySyncSettingsWrapper>
    );
  }

  if (isEditing) {
    return (
      <InventorySyncSettingsWrapper showContainer={false}>
        <EditInventorySync
          inventorySyncFeatureEnabled={inventorySyncFeatureEnabled}
          isLoading={isLoading}
          moveableConnections={moveableConnections}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </InventorySyncSettingsWrapper>
    );
  }

  if (noConnectionsSetup) {
    return (
      <InventorySyncSettingsWrapper handleEditClick={handleEditClick} noConnectionsSetup showContainer>
        <div className={styles['connections-placeholder']}>
          <img
            alt={intl.formatMessage(messages.inventorySyncFlowchartAltText)}
            className={styles['inventory-sync-placeholder']}
            src={InventorySyncPlaceholder}
          />
          <FormattedMessage {...messages.noConnections} />
          <Button onClick={handleEditClick} theme='primary'>
            <FormattedMessage {...messages.setupInventorySyncBtn} />
          </Button>
        </div>
      </InventorySyncSettingsWrapper>
    );
  }

  const userHasNoSourcesToDisplay = sourceConnections.length === 0;
  // Either they don't have the Inventory Sync add-on, or they have no order sources configured yet
  const userHasNoTargetsToDisplay = !inventorySyncFeatureEnabled || targetConnections.length === 0;

  return (
    <InventorySyncSettingsWrapper
      handleEditClick={handleEditClick}
      isSyncButtonDisabled={isSyncButtonDisabled}
      isSystemAdmin={isSystemAdmin}
      onSyncClicked={onSyncClicked}
      showContainer
    >
      <>
        <div className={styles['sync-header-row']}>
          <div className={styles['auto-sync-container']}>
            <div style={{ display: 'none' }}>
              <CheckboxSlider
                checkboxId='automatic-sync-checkbox'
                // blocked and will be enabled when backend is done
                // todo: https://auctane.atlassian.net/browse/SPD-18057
                checked
                disabled
                // checked={enableAutoSync}
                // disabled={!isAutoSyncEnabled}
                labelText={<FormattedMessage {...messages.automaticSync} />}
                // useConsolidatedInventorySources is treated as a source of truth
                // that's why after toggling the switch we need to fetch the data again
                // to keep source of truth in sync with the backend
                // maybe it is worth to rewrite the hook to a context
                onChange={(value) => onToggleAutoSync(value, fetchConsolidatedInventorySources)}
              />
              <Tooltip position='bottom'>
                <span className={styles['auto-sync-tooltip']}>
                  <FontAwesomeIcon className={styles['auto-sync-icon-info']} icon={faInfoCircle} />
                </span>
                <TooltipContent arrowClass='down' contentClassName={styles['auto-sync-tooltip-info']} lightTheme position='bottom'>
                  <Text className={styles['tooltip-content-item']} textType={TextType.Caption} value={messages.automaticSync} />
                  <Text className={styles['tooltip-content-item']} textType={TextType.Caption} value={messages.automaticSyncDescription} />
                  <Text className={styles['tooltip-content-item']} textType={TextType.Caption} value={messages.automaticSyncDescription2} />
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          {/* this div is not redundant
            it keeps the button and the tooltip in the same position
            without it when the button is disabled the tooltip will be misplaced */}
          <div>
            <Button
              className={styles['trigger-sync-btn']}
              disabled={onInventorySyncLoading}
              disabledTooltipMessage={<Text textType={TextType.Caption} value={messages.manualJobIsRunning} />}
              disabledTooltipMessagePosition='right'
              onClick={onInventorySync}
              theme='secondary'
            >
              <FontAwesomeIcon className={styles['trigger-sync-icon']} icon={faArrowsRotate} />
              <FormattedMessage {...messages.initiateManualSync} />
            </Button>
          </div>
        </div>
        <div className={styles['grid-body']}>
          <div className={styles['source-column']}>
            <div className={styles['source-header']}>
              <FormattedMessage {...messages.sourceLabel} />
            </div>
            {userHasNoSourcesToDisplay ? (
              <InventorySetupPanel />
            ) : (
              sourceConnections.map((conn) => (
                <div key={conn.connectionId} className={styles['connection-item']}>
                  <div className={styles['connection-info']}>
                    <div className={styles['connection-header']}>
                      <DataDrivenLogo className={styles['connection-icon']} logoType='icon' {...getLogoProps(conn.apiCode)} />
                      <div className={styles['connection-name']}>{conn.name}</div>
                    </div>
                    {renderSyncStatus(conn)}
                  </div>
                </div>
              ))
            )}
          </div>
          <FontAwesomeIcon className={styles['right-arrow']} icon={faArrowRight} />
          <div className={styles['target-column']}>
            <div className={styles['target-header']}>
              <FormattedMessage {...messages.targetLabel} />
            </div>
            {userHasNoTargetsToDisplay ? (
              <InventorySyncSetupPanel inventorySyncFeatureEnabled={inventorySyncFeatureEnabled} isEditing={false} />
            ) : (
              targetConnections.map((conn) => (
                <div key={conn.connectionId} className={styles['connection-item']}>
                  <div className={styles['connection-info']}>
                    <div className={styles['connection-header']}>
                      <DataDrivenLogo className={styles['connection-icon']} logoType='icon' {...getLogoProps(conn.apiCode)} />
                      <div className={styles['connection-name']}>{conn.name}</div>
                    </div>
                    {renderSyncStatus(conn)}
                  </div>
                  <InventoryConnectionActions connection={conn} onSaveConfig={fetchConsolidatedInventoryConnections} />
                </div>
              ))
            )}
          </div>
        </div>
      </>
    </InventorySyncSettingsWrapper>
  );
};

InventorySyncSettingsWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  showContainer: PropTypes.bool,
  noConnectionsSetup: PropTypes.bool,
  handleEditClick: PropTypes.func,
  isSyncButtonDisabled: PropTypes.bool,
  isSystemAdmin: PropTypes.bool,
  onSyncClicked: PropTypes.func,
};
